/* Scenarios — a board mid-game, and something to achieve from it (#94).
 *
 * "Your opponent is at 1 Life behind a 7000 Blocker. Win this turn." There is
 * a line that does it, and the player has to find it. This is the teaching
 * half of the app: the log already explains what *did* happen, and a scenario
 * asks what *should*.
 *
 * ---------------------------------------------------------------------------
 * Why a scenario is a description and not a saved game
 *
 * The obvious design is to dump `G` to JSON and load it back. It does not work
 * and would not be nice if it did:
 *
 *   - `G.rng` is a closure (see rng.js), and `G.cards` is the whole ~2,700-card
 *     database. Neither survives `JSON.stringify`, so "just serialize G" is
 *     already false today.
 *   - A dumped `G` pins the scenario to one version of the engine's internals.
 *     Rename a field and every scenario ever authored is silently wrong.
 *   - Nobody can write or review one by hand, and hand-authoring is the whole
 *     point — a *teaching* puzzle needs a specific lesson, which is not
 *     something you find by dumping a random position.
 *
 * So a scenario says what is on the table, and `buildScenario` constructs the
 * game from it. Engine internals stay free to move; the format is the contract.
 *
 * ---------------------------------------------------------------------------
 * The one rule this file exists to enforce
 *
 * **A scenario with no solution is the worst bug this feature can have.** It
 * does not look broken — it looks like the player cannot read the board, which
 * is the exact opposite of teaching them. So the format carries the intended
 * `solution` as data, and `test/scenario.test.mjs` replays it and asserts the
 * goal actually fires. A scenario nobody has proved solvable does not ship.
 *
 * The second-worst is a board where *every* line wins, which no solvability
 * check can see: it passes, and asks the player nothing. So the format also
 * carries `traps` — the naive lines that must lose — and `npm run scenarios`
 * plays them out with the opponent answering for itself.
 */
import {
  createGame, apply, legalActions, other, findChar, charPower, leaderPower,
} from './engine.js';
import { lifeTotal, donDeckSize } from './cardtext.js';

/* Deliberately small. Each one is checkable from `G` alone, with no engine
 * changes, which is what keeps a scenario from becoming a second rules system
 * living beside the first. */
export const GOAL_KINDS = ['winThisTurn', 'survive', 'koAll', 'lifeAtMost'];

const isInt = v => Number.isInteger(v);
const isId = v => typeof v === 'string' && /^[A-Za-z0-9]{1,8}-[A-Za-z0-9]{1,8}$/.test(v);

/* ---------------------------------------------------------------------- */
/* the format                                                              */
/* ---------------------------------------------------------------------- */

/**
 * Validate and normalise one scenario.
 *
 * Throws rather than coercing, for the same reason `setfile.js` does: a
 * scenario is authored by us, so a bad field is a bug to fix at the source.
 * Silently defaulting a Life total is how a puzzle becomes unsolvable without
 * anyone noticing.
 */
export function normalizeScenario(raw) {
  if (!raw || typeof raw !== 'object') throw new TypeError('scenario is not an object');

  const id = String(raw.id ?? '').trim();
  if (!/^[a-z0-9][a-z0-9-]{2,63}$/.test(id)) {
    throw new TypeError(`bad scenario id: ${JSON.stringify(raw.id)} (lowercase, dashes, 3-64 chars)`);
  }

  const name = String(raw.name ?? '').trim();
  if (!name) throw new TypeError(`${id}: missing name`);

  const brief = String(raw.brief ?? '').trim();
  if (!brief) throw new TypeError(`${id}: missing brief — the player has to be told what to do`);

  const difficulty = raw.difficulty ?? 1;
  if (!isInt(difficulty) || difficulty < 1 || difficulty > 5) {
    throw new TypeError(`${id}: difficulty must be an integer 1-5`);
  }

  const goal = normalizeGoal(id, raw.goal);
  const state = normalizeState(id, raw.state);

  /* `solution` is the author's own winning line, replayed by the test suite to
   * prove the scenario can be finished at all. Optional in the type sense and
   * mandatory in practice — `provenSolvable` below is what the suite asserts,
   * and a scenario without one cannot be proved and must not ship. */
  const solution = raw.solution == null ? null : normalizeSolution(id, raw.solution);

  /* The lines that must *not* work.
   *
   * `solution` proves the puzzle can be finished. It cannot prove the puzzle
   * is a puzzle — a board where every line wins asks nothing of the player and
   * still passes every check above. So the obvious wrong answer is recorded
   * too, and `npm run scenarios` replays it and fails if it solves.
   *
   * Optional in the type sense. In practice a puzzle without one has never had
   * its trap tested by anything but its author. */
  const traps = normalizeTraps(id, raw.traps);

  return {
    id, name, brief, difficulty, goal, state, solution, traps,
    /* Author's note on what the puzzle teaches. Never shown before the player
     * solves it — the hint is the puzzle. */
    lesson: raw.lesson == null ? null : String(raw.lesson),
  };
}

function normalizeGoal(id, goal) {
  if (!goal || typeof goal !== 'object') throw new TypeError(`${id}: missing goal`);
  const kind = String(goal.kind ?? '');
  if (!GOAL_KINDS.includes(kind)) {
    throw new TypeError(`${id}: unknown goal kind ${JSON.stringify(goal.kind)}`);
  }
  const out = { kind };
  if (kind === 'survive') {
    if (!isInt(goal.turns) || goal.turns < 1) throw new TypeError(`${id}: survive needs turns >= 1`);
    out.turns = goal.turns;
    out.lifeAtLeast = isInt(goal.lifeAtLeast) ? goal.lifeAtLeast : 1;
  }
  if (kind === 'lifeAtMost') {
    if (!isInt(goal.life) || goal.life < 0) throw new TypeError(`${id}: lifeAtMost needs life >= 0`);
    out.life = goal.life;
  }
  return out;
}

function normalizeSolution(id, sol) {
  if (!Array.isArray(sol)) throw new TypeError(`${id}: solution must be an array of actions`);
  for (const a of sol) {
    if (!a || typeof a !== 'object' || !a.type) {
      throw new TypeError(`${id}: every solution step needs a type`);
    }
  }
  return sol.map(a => ({ ...a }));
}

function normalizeTraps(id, traps) {
  if (traps == null) return [];
  if (!Array.isArray(traps)) throw new TypeError(`${id}: traps must be an array`);
  return traps.map((t, i) => {
    const where = `${id}.traps[${i}]`;
    if (!t || typeof t !== 'object') throw new TypeError(`${where} is not an object`);
    const why = String(t.why ?? '').trim();
    /* Named, because the failure message has to be readable by whoever broke
     * it: "the trap solves it" says nothing, "play the 4000 body first solves
     * it" says which assumption stopped holding. */
    if (!why) throw new TypeError(`${where}: a trap has to say what the naive line is`);
    return { why, line: normalizeSolution(where, t.line) };
  });
}

/** One player's half of the board. */
function normalizeSide(id, who, raw) {
  if (!raw || typeof raw !== 'object') throw new TypeError(`${id}: missing ${who}`);
  if (!isId(raw.leader)) throw new TypeError(`${id}.${who}: leader must be a card id`);

  const list = (v, field) => {
    if (v == null) return [];
    if (!Array.isArray(v)) throw new TypeError(`${id}.${who}.${field} must be an array`);
    for (const c of v) if (!isId(c)) throw new TypeError(`${id}.${who}.${field}: bad card id ${JSON.stringify(c)}`);
    return [...v];
  };

  const chars = (raw.chars ?? []).map((c, i) => {
    const where = `${id}.${who}.chars[${i}]`;
    if (!c || typeof c !== 'object') throw new TypeError(`${where} is not an object`);
    if (!isId(c.id)) throw new TypeError(`${where}: bad card id ${JSON.stringify(c.id)}`);
    const don = c.don ?? 0;
    if (!isInt(don) || don < 0) throw new TypeError(`${where}: don must be a non-negative integer`);
    return {
      id: c.id,
      don,
      rested: !!c.rested,
      /* Characters placed by a scenario are assumed to have been on the field
       * since before this turn, so they can attack. An author who wants a
       * summoning-sick body says so. */
      justPlayed: !!c.justPlayed,
    };
  });
  if (chars.length > 5) throw new TypeError(`${id}.${who}: the Character area holds at most 5`);

  const num = (v, field, dflt = 0) => {
    const n = v ?? dflt;
    if (!isInt(n) || n < 0) throw new TypeError(`${id}.${who}.${field} must be a non-negative integer`);
    return n;
  };

  return {
    leader: raw.leader,
    name: raw.name == null ? null : String(raw.name),
    hand: list(raw.hand, 'hand'),
    /* Life is a list of face-down cards, and which cards they are matters —
     * a [Trigger] fires when one is taken. An author who does not care can
     * give a count and get filler from the deck. */
    life: Array.isArray(raw.life) ? list(raw.life, 'life') : num(raw.life, 'life'),
    trash: list(raw.trash, 'trash'),
    deck: list(raw.deck, 'deck'),
    chars,
    stage: raw.stage == null ? null : (isId(raw.stage) ? raw.stage : (() => {
      throw new TypeError(`${id}.${who}.stage must be a card id`);
    })()),
    donActive: num(raw.donActive, 'donActive'),
    donRested: num(raw.donRested, 'donRested'),
    leaderDon: num(raw.leaderDon, 'leaderDon'),
    leaderRested: !!raw.leaderRested,
  };
}

function normalizeState(id, raw) {
  if (!raw || typeof raw !== 'object') throw new TypeError(`${id}: missing state`);
  const turn = raw.turn ?? 3;
  if (!isInt(turn) || turn < 1) throw new TypeError(`${id}: turn must be an integer >= 1`);
  const active = raw.active ?? 'p1';
  if (active !== 'p1' && active !== 'p2') throw new TypeError(`${id}: active must be p1 or p2`);
  return {
    turn,
    active,
    /* Scenarios are Main-Phase puzzles by default. The other phases exist but
     * nothing has wanted one yet, so this is a default rather than a setting
     * with no reason behind it. */
    phase: raw.phase ?? 'main',
    p1: normalizeSide(id, 'p1', raw.p1),
    p2: normalizeSide(id, 'p2', raw.p2),
  };
}

/* ---------------------------------------------------------------------- */
/* building the board                                                      */
/* ---------------------------------------------------------------------- */

/* Every id a scenario mentions, so a caller can check the database has them
 * before trying to build — a missing id must be a clear error, not a stub
 * body the puzzle is then unsolvable against. */
export function scenarioCardIds(sc) {
  const ids = new Set();
  for (const who of ['p1', 'p2']) {
    const s = sc.state[who];
    ids.add(s.leader);
    for (const c of s.hand) ids.add(c);
    for (const c of s.trash) ids.add(c);
    for (const c of s.deck) ids.add(c);
    for (const c of s.chars) ids.add(c.id);
    if (Array.isArray(s.life)) for (const c of s.life) ids.add(c);
    if (s.stage) ids.add(s.stage);
  }
  return [...ids];
}

/**
 * Turn a scenario into a live game.
 *
 * Built through `createGame` and then overwritten, rather than assembling the
 * state object by hand: `createGame` is the one place that knows what a side
 * is made of, and a second constructor here would drift from it the first time
 * a field is added.
 *
 * The deck is *not* shuffled. A puzzle whose answer depends on the top of the
 * deck must be reproducible, and an author who wants an unknown draw can leave
 * the deck as filler — the point is that the scenario decides, not the seed.
 */
export function buildScenario(sc, cards) {
  const missing = scenarioCardIds(sc).filter(id => !cards[id]);
  if (missing.length) {
    throw new TypeError(`${sc.id}: card database is missing ${missing.join(', ')}`);
  }

  const G = createGame({
    cards,
    seed: 1,
    firstPlayer: sc.state.active,
    p1: { name: sc.state.p1.name || 'You', leaderId: sc.state.p1.leader, deck: { [sc.state.p1.leader]: 1 } },
    p2: { name: sc.state.p2.name || 'Opponent', leaderId: sc.state.p2.leader, deck: { [sc.state.p2.leader]: 1 } },
  });

  G.turn = sc.state.turn;
  G.phase = sc.state.phase;
  G.active = sc.state.active;
  /* `createGame` runs `beginTurn`, which draws, adds DON!! and writes a few
   * lines. None of that happened in the fiction the scenario describes. */
  G.log = [];
  G.queue = [];
  G.pending = null;
  G.over = null;
  G.combat = null;

  for (const who of ['p1', 'p2']) applySide(G, who, sc.state[who], cards);
  return G;
}

function applySide(G, who, spec, cards) {
  const S = G[who];
  const leader = cards[spec.leader];

  S.leaderId = spec.leader;
  if (spec.name) S.name = spec.name;
  S.hand = [...spec.hand];
  S.trash = [...spec.trash];
  S.deck = [...spec.deck];
  S.stage = spec.stage;
  S.donActive = spec.donActive;
  S.donRested = spec.donRested;
  S.leaderDon = spec.leaderDon;
  S.leaderRested = spec.leaderRested;
  S.leaderMods = [];
  S.leaderFlags = [];
  S.once = {};
  S.mulliganed = true;              // a mid-game board is past the mulligan
  S.turnsTaken = Math.max(1, Math.floor(G.turn / 2));
  S.donDeck = Math.max(0, donDeckSize(leader) - spec.donActive - spec.donRested - spec.leaderDon);

  /* A Life count rather than a list means "the cards do not matter here", so
   * fill from the deck the way a real game would. Falling back to the Leader's
   * own id keeps every entry a real card — Life is revealed when taken, and a
   * null there would reach the trigger check. */
  if (Array.isArray(spec.life)) {
    S.life = [...spec.life];
  } else {
    S.life = [];
    for (let i = 0; i < spec.life; i++) S.life.push(S.deck.pop() ?? spec.leader);
  }
  const max = lifeTotal(leader);
  if (S.life.length > max) {
    throw new TypeError(`${who}: ${S.life.length} Life cards, but this Leader starts with ${max}`);
  }

  S.chars = spec.chars.map(c => ({
    uid: ++G.uidSeq,
    id: c.id,
    don: c.don,
    rested: c.rested,
    /* `bornTurn` is what summoning sickness reads. A scenario Character has
     * been there since before now unless the author says otherwise. */
    bornTurn: c.justPlayed ? G.turn : G.turn - 2,
    mods: [], baseMods: [], costMods: [], flags: [],
  }));
}

/* ---------------------------------------------------------------------- */
/* did they do it?                                                         */
/* ---------------------------------------------------------------------- */

/**
 * Where the player stands: 'solved', 'failed', or 'open'.
 *
 * Pure, and read off `G` alone. It is called after every action, so it must
 * not mutate anything or care how the position was reached.
 *
 * `you` is the side the player controls, which is always p1 in the UI but is
 * a parameter so the checker can be tested from both seats.
 */
export function goalStatus(G, sc, { you = 'p1', startTurn = sc.state.turn } = {}) {
  const foe = other(you);
  const goal = sc.goal;

  // Losing ends every scenario, whatever the goal was.
  if (G.over && G.over.winner === foe) return 'failed';

  switch (goal.kind) {
    case 'winThisTurn':
      if (G.over?.winner === you) return 'solved';
      /* The turn passing is the failure. Checked on the turn counter rather
       * than the phase, so ending your turn and the opponent taking theirs
       * both land here. */
      return G.turn > startTurn ? 'failed' : 'open';

    case 'lifeAtMost':
      if (G.over?.winner === you) return 'solved';
      if (G[foe].life.length <= goal.life) return 'solved';
      return G.turn > startTurn ? 'failed' : 'open';

    case 'koAll':
      if (G.over?.winner === you) return 'solved';
      if (G[foe].chars.length === 0) return 'solved';
      return G.turn > startTurn ? 'failed' : 'open';

    case 'survive': {
      if (G[you].life.length < goal.lifeAtLeast) return 'failed';
      if (G.turn >= startTurn + goal.turns) return 'solved';
      return 'open';
    }

    default:
      return 'open';
  }
}

/**
 * Let the other side play until it is the player's move again.
 *
 * `policy` is injected rather than imported. Every caller that needs one wants
 * `SKILL.solid` — the same policy the board runs and the same one every
 * published matchup number was measured with — but importing the AI here would
 * put a decision-making module inside the pure description of a board, and
 * `scenario.js` is what the browser loads to *build* a puzzle. The gate is the
 * only thing that has to prove a line.
 *
 * It plays whole turns, not just prompts. Answering the opponent's `pending`
 * is enough for a puzzle on your own turn, where the opponent only ever
 * responds; a defensive puzzle needs them to actually take their turn, or the
 * line stalls at their Main Phase and `survive` can never come true.
 *
 * The guard is a stall breaker, not a rule. A policy that returns nothing, or
 * an engine state nobody can act on, has to end the replay rather than hang
 * the gate.
 */
export function runOpponent(G, policy, { you = 'p1', limit = 400 } = {}) {
  if (!policy) return 0;
  const foe = other(you);
  const theirMove = () => !G.over && (G.pending ? G.pending.side === foe : G.active === foe);

  let acted = 0;
  while (theirMove() && acted < limit) {
    const action = policy(G);
    if (!action) break;
    apply(G, action);
    acted++;
  }
  return acted;
}

/**
 * Replay an authored line and report what happened.
 *
 * This is what proves a scenario is solvable, what `npm run scenarios` runs
 * over every shipped puzzle, and — with `line` pointed at a trap — what proves
 * the puzzle *is* a puzzle. One implementation for both, because two would
 * drift and the difference between them is only which answer is the pass.
 *
 * Deliberately strict: a step that is not currently legal is a broken
 * scenario, not something to skip past.
 *
 * `opponent` is what makes a defensive puzzle checkable at all (#105). On your
 * own turn the opponent only responds, and a line that recorded their answers
 * would stop being the *player's* line the moment the AI changed. On their
 * turn the player's only moves are answers — block, counter, decline — with
 * the opponent's attacks in between, so those attacks have to come from
 * somewhere. Handing them to the policy is what keeps the recorded line the
 * player's own.
 *
 * That does tie a defensive puzzle to the AI's behaviour. It is a real
 * dependency and the honest way to hold it: the gate replays every puzzle on
 * every commit, so a policy change that invalidates one fails loudly instead
 * of shipping a puzzle whose opponent no longer cooperates.
 *
 * @param {object} [o]
 * @param {string} [o.you]        the seat the player controls
 * @param {object[]} [o.line]     what to replay; defaults to the solution
 * @param {(G)=>object} [o.opponent]  plays the other seat; omit for none
 */
export function replaySolution(sc, cards, { you = 'p1', line = sc.solution, opponent = null } = {}) {
  if (!line) return { ok: false, reason: 'no solution recorded', steps: 0, stoppedAt: null };

  const G = buildScenario(sc, cards);
  let steps = 0;

  /**
   * Hand the turn over, but only while the puzzle is still live.
   *
   * The guard is load-bearing, not tidiness. A puzzle is over the moment its
   * goal resolves, and letting the opponent play on past that point scores
   * something that is no longer the puzzle: their deck is three cards of
   * filler, so a few more turns and they deck out and *lose* — which would
   * report a failed trap as a win, and every "win this turn" puzzle as
   * solvable by ending your turn and waiting. The board does the same thing,
   * where it freezes the AI on a decided scenario.
   */
  const settle = () => {
    if (goalStatus(G, sc, { you }) === 'open') runOpponent(G, opponent, { you });
  };

  /* Before the first step as well as after each one: a defensive puzzle opens
   * on the opponent's turn, and the player's first move is an answer to
   * something that has not happened yet. */
  settle();

  for (const action of line) {
    if (G.over) break;
    /* A prompt is answered by the next step, the same way a person does it —
     * `legalActions` returns the answers to `G.pending` when one is open. */
    const match = legalActions(G).find(a => sameAction(a, action));
    if (!match) {
      return {
        ok: false,
        reason: `step ${steps + 1} (${describe(action)}) is not legal here`,
        steps,
        stoppedAt: steps + 1,
        status: goalStatus(G, sc, { you }),
        G,
      };
    }
    /* The engine's own object, not the author's: an authored step names only
     * what identifies the action and leaves the rest for the engine to fill
     * in. Applying the shorthand would drop those fields. */
    apply(G, match);
    steps++;
    settle();
  }

  const status = goalStatus(G, sc, { you });
  return {
    ok: status === 'solved',
    reason: status === 'solved' ? null : `line ended ${status}`,
    steps, stoppedAt: null, status, G,
  };
}

/* Structural comparison, because `legalActions` builds fresh objects. Only the
 * fields that identify an action are compared — an authored step may leave off
 * anything the engine fills in. */
function sameAction(a, b) {
  if (a.type !== b.type) return false;
  for (const k of ['index', 'uid', 'attacker', 'target', 'value', 'activate']) {
    if (b[k] === undefined) continue;
    if (k === 'target') {
      if (JSON.stringify(a.target) !== JSON.stringify(b.target)) return false;
      continue;
    }
    if (a[k] !== b[k]) return false;
  }
  return true;
}

const describe = a => `${a.type}${a.uid ? ` uid=${a.uid}` : ''}${a.index != null ? ` #${a.index}` : ''}`;

/* ---------------------------------------------------------------------- */
/* a hint that is not the answer                                           */
/* ---------------------------------------------------------------------- */

/**
 * How close the board is, for a nudge rather than a solution.
 *
 * Returns the damage still needed and the total power that could still be
 * pointed at the Leader this turn. "You have enough power on the table, you
 * are spending it wrong" is a useful thing to be told; the line is not.
 */
export function pressure(G, you = 'p1') {
  const foe = other(you);
  const lp = leaderPower(G, foe);
  const attackers = G[you].chars
    .filter(c => !c.rested)
    .map(c => charPower(G, you, c));
  if (!G[you].leaderRested) attackers.push(leaderPower(G, you));
  return {
    lifeLeft: G[foe].life.length,
    leaderPower: lp,
    swings: attackers.length,
    connecting: attackers.filter(p => p >= lp).length,
  };
}
